import {z} from "zod";
import {i18n} from "@/i18n-config";
import type {Locale} from "@/i18n-config";
import {isSafeNotificationHref} from "@/utils/notifications/href";

export const NOTIFICATION_TYPES = [
    "account_verified",
    "export_started",
    "export_ready",
    "text",
    "release_note",
] as const;

export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const NOTIFICATION_AUDIENCES = [
    "user",
    "business",
    "agent",
] as const;

export const notificationAudienceNameSchema = z.enum(NOTIFICATION_AUDIENCES);
export type NotificationAudienceName = z.infer<
    typeof notificationAudienceNameSchema
>;

export const notificationAudienceSchema = z
    .array(notificationAudienceNameSchema)
    .min(1)
    .refine((audience) => new Set(audience).size === audience.length, {
        message: "Notification audiences must be unique.",
    });

export type NotificationAudience = z.infer<typeof notificationAudienceSchema>;

export const USER_NOTIFICATION_AUDIENCE: NotificationAudience = ["user"];

export const BUSINESS_USERS_NOTIFICATION_AUDIENCE: NotificationAudience = [
    "business",
];

export const notificationScopeSchema = z.enum(["account", "audience"]);
export type NotificationScope = z.infer<typeof notificationScopeSchema>;

const notificationReferenceIdSchema = z.string().trim().min(1).max(255);

export const accountVerifiedNotificationPayloadSchema = z.object({
    reference: z.object({
        type: z.literal("accounts_verified"),
        id: z.string().uuid(),
    }),
    verifiedAt: z.string().datetime(),
    source: z.enum(["user", "management"]),
});

export type AccountVerifiedNotificationPayload = z.infer<
    typeof accountVerifiedNotificationPayloadSchema
>;

export const exportStartedNotificationPayloadSchema = z.object({
    reference: z.object({
        type: z.literal("exports"),
        id: z.string().uuid(),
    }),
    reportName: z.string().trim().min(1).max(255),
    startedAt: z.string().datetime(),
});

export type ExportStartedNotificationPayload = z.infer<
    typeof exportStartedNotificationPayloadSchema
>;

export const exportReadyNotificationPayloadSchema = z.object({
    reference: z.object({
        type: z.literal("exports"),
        id: z.string().uuid(),
    }),
    reportName: z.string().trim().min(1).max(255),
    fileName: z.string().trim().min(1).max(255),
    completedAt: z.string().datetime(),
});

export type ExportReadyNotificationPayload = z.infer<
    typeof exportReadyNotificationPayloadSchema
>;

type LocalizedNotificationValue =
    & Record<typeof i18n.defaultLocale, string>
    & Partial<Record<Locale, string>>;

function createLocalizedValueSchema(valueSchema: z.ZodType<string>) {
    const shape = Object.fromEntries(
        i18n.locales.map((locale) => [
            locale,
            locale === i18n.defaultLocale
                ? valueSchema
                : valueSchema.optional(),
        ]),
    );

    return z.object(shape) as unknown as z.ZodType<LocalizedNotificationValue>;
}

function createLocalizedTextSchema(maximumLength: number) {
    return createLocalizedValueSchema(
        z.string().trim().min(1).max(maximumLength),
    );
}

export const localizedNotificationTextSchema =
    createLocalizedTextSchema(500);
export const localizedNotificationCategorySchema =
    createLocalizedTextSchema(80);
export const localizedNotificationActionLabelSchema =
    createLocalizedTextSchema(200);
export const localizedNotificationDescriptionSchema =
    createLocalizedTextSchema(5_000);
export type LocalizedNotificationText = LocalizedNotificationValue;
export type LocalizedNotificationCategory = LocalizedNotificationValue;
export type LocalizedNotificationDescription = LocalizedNotificationValue;

export const safeNotificationHrefSchema = z.string().trim().max(2048).refine(
    isSafeNotificationHref,
    "Notification links must be relative paths or HTTPS URLs.",
);
const localizedNotificationActionHrefValueSchema =
    createLocalizedValueSchema(safeNotificationHrefSchema);
export const localizedNotificationActionHrefSchema = z.preprocess(
    (value) => typeof value === "string"
        ? {[i18n.defaultLocale]: value}
        : value,
    localizedNotificationActionHrefValueSchema,
);
export type LocalizedNotificationActionHref = LocalizedNotificationValue;

export const notificationCategoryKeySchema = z.string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{0,63}$/);
export type NotificationCategoryKey = z.infer<
    typeof notificationCategoryKeySchema
>;

export const notificationReferenceSchema = z.object({
    type: z.string().trim().regex(/^[a-z][a-z0-9_]{0,63}$/),
    id: notificationReferenceIdSchema,
});

export const textNotificationActionSchema = z.object({
    label: localizedNotificationActionLabelSchema,
    href: localizedNotificationActionHrefSchema,
});

export type TextNotificationAction = z.infer<
    typeof textNotificationActionSchema
>;

export const textNotificationContentSchema = z.object({
    categoryKey: notificationCategoryKeySchema.optional(),
    category: localizedNotificationCategorySchema,
    title: localizedNotificationTextSchema,
    description: localizedNotificationDescriptionSchema.optional(),
    action: textNotificationActionSchema.optional(),
});

export const textNotificationPayloadSchema = textNotificationContentSchema.extend({
    reference: notificationReferenceSchema,
});

export type TextNotificationPayload = z.infer<
    typeof textNotificationPayloadSchema
>;

export const releaseNoteNotificationPayloadSchema = z.object({
    reference: z.object({
        type: z.literal("release_notes"),
        id: notificationReferenceIdSchema,
    }),
    title: localizedNotificationTextSchema,
    description: localizedNotificationDescriptionSchema.optional(),
    version: z.string().trim().min(1).max(64).optional(),
    href: safeNotificationHrefSchema,
});

export type ReleaseNoteNotificationPayload = z.infer<
    typeof releaseNoteNotificationPayloadSchema
>;

const notificationRowBaseSchema = z.object({
    id: z.string().uuid(),
    audience: notificationAudienceSchema,
    published_at: z.string().datetime({offset: true}),
    expires_at: z.string().datetime({offset: true}).nullable(),
    read_at: z.string().datetime({offset: true}).nullable(),
    sticky: z.boolean().optional(),
});

export const accountVerifiedNotificationSchema = notificationRowBaseSchema.extend({
    type: z.literal("account_verified"),
    payload: accountVerifiedNotificationPayloadSchema,
});

export const exportStartedNotificationSchema = notificationRowBaseSchema.extend({
    type: z.literal("export_started"),
    payload: exportStartedNotificationPayloadSchema,
});

export const exportReadyNotificationSchema = notificationRowBaseSchema.extend({
    type: z.literal("export_ready"),
    payload: exportReadyNotificationPayloadSchema,
});

export const textNotificationSchema = notificationRowBaseSchema.extend({
    type: z.literal("text"),
    payload: textNotificationPayloadSchema,
});

export const releaseNoteNotificationSchema = notificationRowBaseSchema.extend({
    type: z.literal("release_note"),
    payload: releaseNoteNotificationPayloadSchema,
});

export const accountNotificationSchema = z.discriminatedUnion("type", [
    accountVerifiedNotificationSchema,
    exportStartedNotificationSchema,
    exportReadyNotificationSchema,
    textNotificationSchema,
    releaseNoteNotificationSchema,
]);

export type AccountVerifiedNotification = z.infer<
    typeof accountVerifiedNotificationSchema
>;
export type ExportStartedNotification = z.infer<
    typeof exportStartedNotificationSchema
>;
export type ExportReadyNotification = z.infer<typeof exportReadyNotificationSchema>;
export type TextNotification = z.infer<typeof textNotificationSchema>;
export type ReleaseNoteNotification = z.infer<typeof releaseNoteNotificationSchema>;
export type AccountNotification = z.infer<typeof accountNotificationSchema>;

export const notificationPayloadSchemas = {
    account_verified: accountVerifiedNotificationPayloadSchema,
    export_started: exportStartedNotificationPayloadSchema,
    export_ready: exportReadyNotificationPayloadSchema,
    text: textNotificationPayloadSchema,
    release_note: releaseNoteNotificationPayloadSchema,
} as const;

export type NotificationPayloadByType = {
    account_verified: AccountVerifiedNotificationPayload;
    export_started: ExportStartedNotificationPayload;
    export_ready: ExportReadyNotificationPayload;
    text: TextNotificationPayload;
    release_note: ReleaseNoteNotificationPayload;
};

type PublishNotificationBase<TType extends NotificationType> = {
    type: TType;
    audience: NotificationAudience;
    payload: NotificationPayloadByType[TType];
    publishedAt?: string;
    expiresAt?: string;
    sticky?: boolean;
};

export type PublishNotificationInput<TType extends NotificationType> =
    PublishNotificationBase<TType> & (
        | {
            scope: "account";
            accountId: string;
        }
        | {
            scope: "audience";
            accountId?: never;
        }
    );

export type AccountNotificationFeed = {
    notifications: AccountNotification[];
    unreadCount: number;
    hasMore?: boolean;
};

export function parseAccountNotifications(rows: unknown[]): AccountNotification[] {
    const notifications: AccountNotification[] = [];

    for (const row of rows) {
        const parsed = accountNotificationSchema.safeParse(row);
        if (parsed.success) {
            notifications.push(parsed.data);
        } else {
            console.error("Ignoring unsupported account notification", {
                issues: parsed.error.issues,
            });
        }
    }

    return notifications;
}
